import { type FC, useState, useCallback, useEffect, type ComponentProps } from 'react';
import classnames from 'classnames';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import { Link } from 'dibs-elements/exports/Link';
import { Badge } from 'dibs-elements/exports/Badge';
import Copy from 'dibs-icons/exports/legacy/Copy';
import Checkmark from 'dibs-icons/exports/legacy/Checkmark';
import { FormattedMessage } from 'dibs-react-intl';
import { shortAddress, etherscanUrl, shortName } from 'dibs-crypto-utils/exports/formattingUtils';

import styles from './NftPublicProfileAddressBadge.scss';

type Size = ComponentProps<typeof Badge>['size'];

type Split<T> = T extends `${infer A}-${infer B}` ? [A, B] : [undefined, undefined];
const splitSize = <T extends string>(arg: T): Split<typeof arg> =>
    (arg?.split('-') || []) as Split<typeof arg>;

export const NftPublicProfileAddressBadge: FC<{
    walletAddress?: string | null;
    ensDomain?: string | null;
    size?: Size;
}> = ({ walletAddress, ensDomain, size = 'medium-heavy' }) => {
    const [hasClipboardApi, setHasClipboardApi] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const copyToClipboard = useCallback(() => {
        try {
            navigator.clipboard.writeText(walletAddress || '').then(
                () => {
                    setIsCopied(true);
                },
                () => {
                    throw new Error();
                }
            );
        } catch (e) {
            // silently fail
        }
    }, [walletAddress]);

    useEffect(() => {
        setHasClipboardApi(typeof navigator !== 'undefined' && !!navigator?.clipboard?.writeText);
    }, []);

    if (!walletAddress) {
        return null;
    }
    const [fontSize, fontWeight] = splitSize(size);

    return (
        <div className={classnames(styles.linkWrapper, styles[fontSize], styles[fontWeight])}>
            <Link target="_blank" href={etherscanUrl('/address/' + walletAddress)}>
                <Badge type="pill" size={size} interactive>
                    <span className={styles.walletAddress}>
                        {shortName(ensDomain || '') || shortAddress(walletAddress)}
                    </span>
                </Badge>
            </Link>

            <div className={styles.copyButtonWrapper}>
                <Tooltip
                    isVisible={!isCopied && isTooltipVisible}
                    type="small"
                    zIndex="high"
                    direction="right"
                >
                    {hasClipboardApi ? (
                        <FormattedMessage
                            defaultMessage="Copy Address"
                            id="nft.profile.copyAddress"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Copy Unavailable"
                            id="nft.profile.copyUnavailable"
                        />
                    )}
                </Tooltip>
                <Link
                    onClick={() => {
                        if (hasClipboardApi) {
                            copyToClipboard();
                            setIsTooltipVisible(false);
                            setTimeout(() => {
                                setIsCopied(false);
                            }, 5000);
                        }
                    }}
                    className={styles.iconWrapper}
                    onMouseEnter={() => setIsTooltipVisible(true)}
                    onMouseLeave={() => setIsTooltipVisible(false)}
                >
                    {isCopied ? (
                        <Checkmark className={`${styles.copyIcon} ${styles.success}`} />
                    ) : (
                        <Copy className={styles.copyIcon} />
                    )}
                </Link>
            </div>
        </div>
    );
};
